import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2, Eye } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FloatingLabelInput } from '@/shared/components/custom/form-field';
import { DynamicFormField } from './DynamicFormField';
import { PublicFormClosedCard } from './PublicFormClosedCard';
import type { SubmissionFormInput } from '../../schemas';
import type { FormField, FormStatus } from '../../types';

interface RespondableForm {
  title: string;
  description: string | null;
  status: FormStatus;
  emailRequired: boolean;
  fields: FormField[];
  expiresAt?: string | null;
}

/** Maps the 4 "form unavailable" backend codes (PR #90) to a specific load-error message. */
const LOAD_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'Form.Expired': 'renderer.error.expired',
  'Form.CannotSubmitOnClosed': 'renderer.error.closed',
  'Form.CannotSubmitOnArchived': 'renderer.error.archived',
  'Form.NotPublished': 'renderer.error.notPublished',
};

interface FormResponseViewProps {
  /** Namespaces `data-testid`s so each entry page (public/internal/token) keeps its own. */
  testIdPrefix: string;
  isLoading: boolean;
  isError: boolean;
  /** Backend error code from the page-load request, when `isError` — picks a specific message. */
  errorCode?: string | null;
  form: RespondableForm | undefined;
  submitted: boolean;
  rhfForm: UseFormReturn<SubmissionFormInput>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  isSubmitting: boolean;
  backAction?: { label: string; onClick: () => void };
  /** Owner preview — shows the form regardless of status, submit button is inert. */
  previewMode?: boolean;
  /** Renders without the full-page wrapper, for use inside a Dialog. */
  embedded?: boolean;
  /** Omits the submit button/hint entirely — for template previews, which have their own dialog footer instead. */
  hideSubmit?: boolean;
}

export function FormResponseView({
  testIdPrefix,
  isLoading,
  isError,
  errorCode = null,
  form,
  submitted,
  rhfForm,
  onSubmit,
  isSubmitting,
  backAction,
  previewMode = false,
  embedded = false,
  hideSubmit = false,
}: FormResponseViewProps) {
  const { t } = useTranslation('forms');
  const sortedFields = form?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  const isClosed = !previewMode && form?.status !== 'Shared';
  const showThankYou = !previewMode && submitted;

  const content = (
    <div className="space-y-4">
      {backAction && (
        <Button
          variant="ghost"
          size="sm"
          onClick={backAction.onClick}
          data-testid={`${testIdPrefix}-back-button`}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backAction.label}
        </Button>
      )}

      {isLoading && (
        <div
          className="space-y-4"
          aria-hidden="true"
          data-testid={`${testIdPrefix}-loading-skeleton`}
        >
          <Card className="overflow-hidden">
            <div className="h-2 bg-primary/30" />
            <CardHeader>
              <Skeleton className="h-7 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
          </Card>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && (isError || !form) && (
        <Card data-testid={`${testIdPrefix}-error-message`}>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {t(
                errorCode
                  ? (LOAD_ERROR_MESSAGE_KEYS[errorCode] ?? 'renderer.loadError')
                  : 'renderer.loadError',
              )}
            </p>
            <p className="text-xs text-muted-foreground">{t('renderer.closed.poweredBy')}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && form && isClosed && (
        <PublicFormClosedCard title={form.title} expiresAt={form.expiresAt} />
      )}

      {!isLoading && form && !isClosed && showThankYou && (
        <div
          className="mx-auto max-w-lg space-y-4 rounded-xl border bg-background p-6 text-center"
          data-testid={`${testIdPrefix}-thank-you`}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold">{t('renderer.thankYouTitle')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('renderer.thankYouDescription', { title: form.title })}
            </p>
          </div>
          {backAction && (
            <>
              <Separator />
              <Button type="button" className="w-full" size="lg" onClick={backAction.onClick}>
                {t('renderer.backToFormsButton')}
              </Button>
            </>
          )}
        </div>
      )}

      {!isLoading && form && !isClosed && !showThankYou && previewMode && (
        <form onSubmit={onSubmit} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
          <Card className="overflow-hidden">
            <div className="h-2 bg-primary" />
            <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{form.title}</h1>
                {form.description && (
                  <p className="text-sm text-muted-foreground">{form.description}</p>
                )}
              </div>
              <Badge variant="secondary" className="flex shrink-0 items-center gap-1">
                <Eye className="h-3 w-3" />
                {t('preview.badge')}
              </Badge>
            </CardHeader>
          </Card>

          {form.emailRequired && (
            <Card>
              <CardContent className="pt-6">
                <FloatingLabelInput
                  id={`${testIdPrefix}-email-input`}
                  label={t('renderer.emailLabel')}
                  type="email"
                  {...rhfForm.register('email')}
                  invalid={!!rhfForm.formState.errors.email}
                  data-testid={`${testIdPrefix}-email-input`}
                />
              </CardContent>
            </Card>
          )}

          {sortedFields.map((field) => (
            <Card key={field.id ?? field.order}>
              <CardContent className="pt-6">
                <DynamicFormField field={field} control={rhfForm.control} />
              </CardContent>
            </Card>
          ))}

          {!hideSubmit && (
            <div className="flex flex-col items-end gap-1.5 pt-2">
              <Button
                type="submit"
                size="lg"
                disabled
                data-testid={`${testIdPrefix}-submit-button`}
              >
                {t('renderer.submitButton')}
              </Button>
              <p className="text-xs text-muted-foreground">{t('preview.disabledSubmitHint')}</p>
            </div>
          )}
        </form>
      )}

      {!isLoading && form && !isClosed && !showThankYou && !previewMode && (
        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-xl border bg-background p-6"
          data-testid={`${testIdPrefix}-form`}
        >
          <div>
            <h1 className="text-lg font-semibold">{form.title}</h1>
            {form.description && (
              <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
            )}
          </div>

          <Separator />

          {form.emailRequired && (
            <FloatingLabelInput
              id={`${testIdPrefix}-email-input`}
              label={t('renderer.emailLabel')}
              type="email"
              {...rhfForm.register('email')}
              invalid={!!rhfForm.formState.errors.email}
              data-testid={`${testIdPrefix}-email-input`}
            />
          )}

          {sortedFields.map((field) => (
            <DynamicFormField
              key={field.id ?? field.order}
              field={field}
              control={rhfForm.control}
            />
          ))}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isSubmitting}
            data-testid={`${testIdPrefix}-submit-button`}
          >
            {t('renderer.submitButton')}
          </Button>
        </form>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-2xl px-4 py-8 md:py-12">{content}</div>
    </div>
  );
}
